import { useProductPageStore } from '../../contexts/ProductPageStore'
import AskQuestionForm from '../qna/askQuestionForm'
import QnaCard from '../qna/qnaCard'
import styles from './qna.module.scss'

import React, { useState } from 'react'

import { createClickedSegmentEvent } from 'Clutch/Utilities/Instrumentation/Impressions/impressionSegmentEvents'
import LayerColorFinder from 'Clutch/Utilities/LayerColorFinder'

import Accordion from 'Clutch/Molecules/Accordion'
import Button from 'Clutch/Molecules/Button'

import Card from 'Clutch/Atoms/Card'
import Select from 'Clutch/Atoms/Form/Elements/Select'
import Panel from 'Clutch/Atoms/Panel'
import TextLink from 'Clutch/Atoms/TextLink'
import Typography from 'Clutch/Atoms/Typography'

const QnaCounts = () => {
  const qnaData = useProductPageStore((x) => x.powerReviewsQnAResponse)

  const questionCount = qnaData?.paging?.total_results || qnaData?.results?.length || 0
  const answerCount = qnaData?.results?.reduce((sum, q) => sum + (q.answer?.length || 0), 0) || 0

  if (questionCount <= 0) return false

  return (
    <Typography size={0.875} tone={'subtle'}>
      {questionCount} {questionCount === 1 ? 'Question' : 'Questions'}
      {' • '}
      {answerCount} {answerCount === 1 ? 'Answer' : 'Answers'}
    </Typography>
  )
}

const QnaSection = () => {
  const isQnaAccordionOpen = useProductPageStore((x) => x.isQuestionsAndAnswersAccordionOpen)

  return (
    <Card fill>
      <Accordion
        intent={'action'}
        iconClosed={'plus'}
        iconOpened={'minus'}
        size={0.875}
        fill
        summary={
          <>
            <Typography as={'h2'} size={1.25} font={'heading'}>
              {'Questions & Answers'}
            </Typography>
            <QnaCounts />
          </>
        }
        segmentAccordionName={'pdp qna'}
        defaultOpen={isQnaAccordionOpen}
        alignIconToTop={true}
      >
        <div>
          <NormalQnaGuts />
        </div>
      </Accordion>
    </Card>
  )
}

const NormalQnaGuts = () => {
  const qnaData = useProductPageStore((x) => x.powerReviewsQnAResponse)
  const backgroundColor = LayerColorFinder(1, null, null, null)
  const focusColor = LayerColorFinder(3, null, null, null)
  const skuBaseNumber = useProductPageStore((x) => x.skuBaseNumber)
  const powerReviewsMerchantId = useProductPageStore((x) => x.powerReviewsMerchantId)
  const powerReviewsApiKey = useProductPageStore((x) => x.powerReviewsApiKey)

  const [showAskForm, setShowAskForm] = useState(false)

  if (!qnaData) return false

  if (!qnaData.results || !qnaData.results.length) {
    return (
      <div>
        <div className={styles.noReviews}>
          <TextLink
            intent={'action'}
            segmentEvent={createClickedSegmentEvent('AskAQuestion')}
            onClick={() => setShowAskForm(!showAskForm)}
          >
            Be the first to ask a question.
          </TextLink>
        </div>
        {showAskForm && (
          <AskQuestionForm
            skuBaseNumber={skuBaseNumber}
            powerReviewsMerchantId={powerReviewsMerchantId}
            powerReviewsApiKey={powerReviewsApiKey}
            onCancel={() => setShowAskForm(false)}
            onSuccess={() => setShowAskForm(false)}
          />
        )}
      </div>
    )
  }

  return (
    <div style={{ '--bg-color': backgroundColor, '--focus-color': focusColor }}>
      <div className={styles.qnaButtonWrapper}>
        <QnaSort />
        <Button
          className={styles.readallreviews_btn}
          brand={'secondary'}
          segmentEvent={createClickedSegmentEvent('AskAQuestion')}
          size={'small'}
          style={{ whiteSpace: 'nowrap' }}
          text={'Ask A Question'}
          onClick={() => setShowAskForm(!showAskForm)}
        />
      </div>
      {showAskForm && (
        <AskQuestionForm
          skuBaseNumber={skuBaseNumber}
          powerReviewsMerchantId={powerReviewsMerchantId}
          powerReviewsApiKey={powerReviewsApiKey}
          onCancel={() => setShowAskForm(false)}
          onSuccess={() => setShowAskForm(false)}
        />
      )}

      {qnaData.results.map((question, i) => (
        <QnaCard
          qnaData={question}
          key={`qna_card_${i}`}
          powerReviewsApiKey={powerReviewsApiKey}
          powerReviewsMerchantId={powerReviewsMerchantId}
          skuBaseNumber={skuBaseNumber}
        />
      ))}
      <LoadMoreButton paging={qnaData.paging} />
    </div>
  )
}

const LoadMoreButton = ({ paging }) => {
  const loadMoreQuestions = useProductPageStore((x) => x.loadMoreQuestions)
  const powerReviewsApiKey = useProductPageStore((x) => x.powerReviewsApiKey)

  if (!paging) return false

  let questionsShown = paging.page_size * paging.current_page_number
  if (questionsShown > paging.total_results) {
    questionsShown = paging.total_results
  }

  let nextPageUrl = null
  if (paging.next_page_url) {
    nextPageUrl = `${paging.next_page_url}&apikey=${powerReviewsApiKey}`
  }

  return (
    <>
      {nextPageUrl && (
        <div className={styles.buttonWrapper}>
          <Button
            className={styles.readallreviews_btn}
            intent={'subdued'}
            segmentEvent={createClickedSegmentEvent('LoadMoreQuestions')}
            type={'div'}
            size={'small'}
            text={'Load More Questions'}
            onClick={() => loadMoreQuestions(nextPageUrl)}
          />
        </div>
      )}
      <Typography size={0.875} tone={'subtle'} className={styles.loadMoreText}>
        {questionsShown} of {paging.total_results} questions shown
      </Typography>
    </>
  )
}

const QnaSort = () => {
  const [sortValue, setSortValue] = useState('Newest')
  const loadFilteredQuestions = useProductPageStore((x) => x.loadFilteredQuestions)

  const sortOptions = [
    { label: 'Most Recent', value: 'Newest' },
    { label: 'Most Helpful', value: 'MostHelpful' },
    { label: 'Oldest', value: 'Oldest' },
  ]

  return (
    <div>
      <Panel layer={0} className={styles.reviewSort}>
        <Select
          id={'qna_sort'}
          testingName={'qna_sort'}
          className={styles.reviewSortSelect}
          layer={1}
          displayLabel={'false'}
          value={sortValue}
          options={sortOptions}
          borderIntent={'subdued'}
          label={'Sort Questions'}
          onChange={(e) => {
            setSortValue(e.target.value)
            loadFilteredQuestions(e.target.value)
          }}
        />
      </Panel>
    </div>
  )
}

export default QnaSection
